/* eslint-disable no-console */
import { PLPURL } from '@tvg/product-listing-url';
import axios, { AxiosError, AxiosRequestConfig } from 'axios';
import { DEFAULT_PRODUCTS_PER_PAGE, DEFAULT_SORT_OPTION } from 'common/config';
import { PLPData, PLPGeneralFacet, PLPSelectedFacetValue } from 'common/types/plp-data';
import type * as SearchResponse from 'common/types/search-response';
import { findFacetValue, isGroupedFacet } from 'common/utils/facets';
import { normalizeBrandFacet } from './normalize-brand-facet';
import { normalizeCategoryType } from './normalize-category-type';

export const getCustomerIDValue = (cookies: string): string | undefined => {
  if (cookies) {
    const cookieName = 'COID';
    const cookieParts = cookies.split(';');

    const matchingCookie = cookieParts.map((cookie) => cookie.trim().split('=')).find(([name]) => name === cookieName);

    if (matchingCookie) {
      return matchingCookie[1];
    }
  }
  return undefined;
};

function clonePLPURLForFacet(plpurl: PLPURL): PLPURL {
  const url = plpurl.href.split('?')[0];
  return new PLPURL(url, plpurl.brand);
}

const transformFacetValues = <T extends Omit<SearchResponse.FacetValue, 'aria_label' | 'count'>>({
  facetId,
  values,
  plpurl,
  selected,
}: {
  facetId: string;
  values: T[];
  plpurl: PLPURL;
  selected?: boolean | PLPSelectedFacetValue[];
}) =>
  values.map((v) => {
    const isSelected = typeof selected === 'boolean' ? selected : Boolean(selected?.some((val) => val.id === v.id));
    const cleanPLPURL = clonePLPURLForFacet(plpurl);
    return {
      ...v,
      selected: isSelected,
      url: cleanPLPURL.generateHref({ facet: { id: facetId, option: v.id }, stripParams: true }),
    };
  });

// Invoke the Search/Browse API, optionally pass headers (for server-side render only as they are not passed be default)
export const getPLPResults = async ({
  plpurl,
  apiURL,
  headers,
  signal,
}: {
  plpurl: PLPURL;
  apiURL: string;
  headers?: Record<string, any>;
  signal?: AbortSignal;
}): Promise<PLPData | AxiosError> => {
  const isBrowser = typeof window !== 'undefined';

  try {
    const requestOptions: AxiosRequestConfig = {
      headers: { 'x-brand': plpurl.brand },
      timeout: 10000,
      signal,
    };

    let customerID;
    // Add all "x-..." headers, cookies, and user-agent
    if (headers) {
      Object.entries(headers).forEach((entry) => {
        const [key, value] = entry;
        const keyName = key.toLowerCase();
        if (keyName.startsWith('x-') || keyName === 'cookie' || keyName === 'user-agent') {
          requestOptions.headers![key] = value;
          if (isBrowser === false && keyName === 'cookie') {
            customerID = getCustomerIDValue(value);
          }
        }
      });
    }

    const url = plpurl.generateAPIUrl(apiURL, { customerId: customerID });
    const res = await axios.get<SearchResponse.SearchResponse>(url, requestOptions);
    const resData = res.data.response;

    normalizeBrandFacet(resData);
    normalizeCategoryType(resData, plpurl);

    const selectedFacets = Object.fromEntries(
      (plpurl.facets ?? []).map((selectedFacet) => {
        const facet = resData.facet_list.find((f) => f.id === selectedFacet.id);
        return [
          selectedFacet.id,
          transformFacetValues({
            facetId: selectedFacet.id,
            selected: true,
            plpurl,
            values: selectedFacet.options.map((v) => {
              const optionId = v.id;
              const facetVal = facet && findFacetValue(facet, optionId);
              if (!facetVal) return { id: optionId, name: optionId };
              return facetVal;
            }),
          }),
        ];
      }),
    );

    const facets = resData.facet_list.map((f) =>
      isGroupedFacet(f)
        ? {
            ...f,
            values: f.values.map((g) => ({
              ...g,
              items: transformFacetValues({
                facetId: f.id,
                values: g.items,
                plpurl,
                selected: selectedFacets[f.id],
              }),
            })),
          }
        : {
            ...f,
            values: transformFacetValues({
              facetId: f.id,
              values: f.values,
              plpurl,
              selected: selectedFacets[f.id],
            }),
          },
    );

    const data: PLPData = {
      parentCategories: resData.meta.parent_categories,
      category: !plpurl.category?.id ? {} : resData.meta.selected_category,
      facets,
      selectedFacets,
      sortBy: plpurl.sortOption || DEFAULT_SORT_OPTION,
      page: resData.meta.request?.page || 1,
      numResults: resData.meta.num_results,
      resultsPerPage: resData.meta.request?.num_results_per_page || DEFAULT_PRODUCTS_PER_PAGE,
      products: resData.product_list,
      collection: resData.meta.collection,
      meta: resData.meta,
      categories: resData.groups_list?.filter((f): f is PLPGeneralFacet => f.type === 'category'),
      links: {
        clearAll: plpurl.clearAllHref,
      },
      rawResponse: resData,
    };

    return data;
  } catch (err) {
    if (err.code === 'ERR_CANCELED') {
      console.info(`Request is ${err.message}, error code: ${err.code}`);
    } else {
      console.error(`${plpurl.type} API response error - ${err}`);
    }

    return err;
  }
};
